/** Building entrance affordances anchored to the same point as interaction. */
import * as pc from 'playcanvas'
import { box, matte, signTexture } from './districtShared'
import {
  plotDoorPosition, plotFrontZ, DOOR_WIDTH, DOOR_HEIGHT,
  INFORMATION_BAND_TOP, type PlotDef,
} from './district'

/**
 * A domain hue strong enough to read as an awning against a pale facade.
 * Plot tints are deliberately washed (they multiply a wall swatch), so they
 * vanish on a white building; darkening lifts them back to a legible accent.
 */
function awningColor(tint: readonly [number, number, number]): pc.Color {
  return new pc.Color(tint[0] * 0.55, tint[1] * 0.55, tint[2] * 0.55)
}

export function buildEntrance(
  app: pc.Application,
  parent: pc.Entity,
  plot: PlotDef,
): void {
  if (!plot.enterable) return
  const layout = entranceLayout(plot)
  const { doorX } = layout
  const front = plotFrontZ(plot)
  // Outward from the building toward the street (matches the frontage normal).
  const outward = plot.side === 'north' ? 1 : -1

  // Civic shells in the licensed set do not expose a street-facing door.
  // Give only those shells a restrained, human-scale entrance at the exact
  // interaction anchor. Shop/Library models retain their authored doors.
  if (layout.doorPanel) {
    const frameMat = matte(new pc.Color(0.08, 0.11, 0.16), 0.55)
    const panelMat = matte(new pc.Color(0.17, 0.32, 0.40), 0.72)
    const hardwareMat = matte(new pc.Color(0.82, 0.64, 0.22), 0.78)
    const portalMat = matte(new pc.Color(0.08, 0.46, 0.56), 0.58)
    const doorInset = plot.entrance?.doorInset ?? 0
    const faceZ = front - outward * doorInset + outward * 0.12
    if (doorInset > 0.1) {
      // Some licensed civic shells expose a deep open portico. A door on the
      // plot line reads as a loose teleport prop, so form a narrow, roofed
      // vestibule and mount the panel against its back wall.
      const passageZ = front - outward * doorInset / 2
      box(parent, `door-${plot.id}-vestibule-left`,
        [doorX - 0.88, 1.35, passageZ],
        [0.18, 2.70, doorInset], frameMat)
      box(parent, `door-${plot.id}-vestibule-right`,
        [doorX + 0.88, 1.35, passageZ],
        [0.18, 2.70, doorInset], frameMat)
      box(parent, `door-${plot.id}-vestibule-roof`,
        [doorX, 2.62, passageZ],
        [1.94, 0.18, doorInset], frameMat)
      for (const x of [doorX - 0.76, doorX + 0.76]) {
        box(parent, `door-${plot.id}-portal-strip-${x}`,
          [x, 1.35, front - outward * 0.04],
          [0.10, 2.56, 0.10], portalMat)
      }
    }
    box(parent, `door-${plot.id}-panel`, [doorX, 1.18, faceZ], [1.44, 2.28, 0.10], panelMat)
    box(parent, `door-${plot.id}-jamb-left`, [doorX - 0.79, 1.22, faceZ + outward * 0.02], [0.14, 2.44, 0.14], frameMat)
    box(parent, `door-${plot.id}-jamb-right`, [doorX + 0.79, 1.22, faceZ + outward * 0.02], [0.14, 2.44, 0.14], frameMat)
    box(parent, `door-${plot.id}-head`, [doorX, 2.38, faceZ + outward * 0.02], [1.72, 0.16, 0.14], frameMat)
    box(parent, `door-${plot.id}-handle`, [doorX + 0.48, 1.14, faceZ + outward * 0.09], [0.08, 0.30, 0.08], hardwareMat)
  }

  // ── Canopy ──────────────────────────────────────────────────────────────
  // A flat awning over the building's own storefront door: wider than the
  // opening, projecting toward the street, coloured for the domain. A thin
  // fascia band hangs off its street edge so it reads as a shopfront awning
  // rather than a shelf.
  const canopyMat = matte(awningColor(plot.tint ?? [0.7, 0.7, 0.7]), 0.35)
  const proj = 1.3
  if (layout.canopyWidth > 0) {
    box(parent, `canopy-${plot.id}`,
      [doorX, layout.canopyY, front + outward * (proj / 2)],
      [layout.canopyWidth, 0.16, proj], canopyMat, true)
    // Valance: the short skirt on the street edge of the canopy.
    box(parent, `canopy-${plot.id}-valance`,
      [doorX, layout.canopyY - 0.22, front + outward * proj],
      [layout.canopyWidth, 0.44, 0.08], canopyMat, true)
  }

  // ── Fascia sign ───────────────────────────────────────────────────────────
  // Names the domain, inside the band a walking player can actually read.
  if (!plot.name) return
  const signMat = new pc.StandardMaterial()
  const tex = signTexture(app, plot.name)
  signMat.diffuse = new pc.Color(1, 1, 1)
  signMat.diffuseMap = tex
  signMat.emissive = new pc.Color(1, 1, 1)
  signMat.emissiveMap = tex
  signMat.emissiveIntensity = 2.2
  signMat.useLighting = false
  signMat.update()

  const sign = box(parent, `sign-${plot.id}`,
    [doorX, layout.signY, front + outward * layout.signOffset],
    [layout.signWidth, layout.signWidth * 96 / 512, 0.08], signMat)
  // The textured face of a box points +Z by default; turn it to the street.
  sign.setEulerAngles(0, plot.side === 'north' ? 0 : 180, 0)
}

export interface EntranceLayout {
  doorX: number
  doorZ: number
  canopyWidth: number
  canopyY: number
  signWidth: number
  signY: number
  signOffset: number
  doorPanel: boolean
}

/** Pure entrance layout shared by rendering and acceptance tests. */
export function entranceLayout(plot: PlotDef): EntranceLayout {
  const [doorX, doorZ] = plotDoorPosition(plot)
  const canopyWidth = plot.entrance?.canopyWidth ??
    Math.min(DOOR_WIDTH + 1.6, plot.maxX - plot.minX - 0.8)
  const canopyY = DOOR_HEIGHT + 0.35
  const signWidth = plot.entrance?.signWidth ?? Math.min(plot.maxX - plot.minX - 1.0, 7.0)
  const signY = plot.entrance?.signY ?? Math.min(canopyY + 1.1, INFORMATION_BAND_TOP + 0.8)
  const signOffset = plot.entrance?.signOffset ?? 0.16
  const doorPanel = plot.entrance?.doorPanel ?? false
  return { doorX, doorZ, canopyWidth, canopyY, signWidth, signY, signOffset, doorPanel }
}
